import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { CaseStatusBadge, CaseProgressStepper, CASE_STATUSES } from '../components/CaseStatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Upload, Download, FileText, Send, Clock, User, DollarSign, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState('');
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState([{ description: '', amount: 0 }]);
  const [invoiceDue, setInvoiceDue] = useState('');
  const [rating, setRating] = useState(0);
  const [ratingComment, setRatingComment] = useState('');
  const [existingRating, setExistingRating] = useState(null);
  const fileInputRef = useRef(null);

  const fetchCase = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cases/${caseId}`, { withCredentials: true });
      setCaseData(res.data);
      setStatusUpdate(res.data.status);
      // Check existing rating
      if (res.data.status === 'resolved' || res.data.status === 'closed') {
        try {
          const ratingsRes = await axios.get(`${API}/ratings?case_id=${caseId}`, { withCredentials: true });
          if (ratingsRes.data.length > 0) setExistingRating(ratingsRes.data[0]);
        } catch {}
      }
    } catch {
      navigate(-1);
    }
  }, [caseId, navigate]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/documents/upload?case_id=${caseId}`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch {}
    }
    setUploading(false);
    fetchCase();
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/documents/${doc.doc_id}/download`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const handleAddNote = async () => {
    if (!note.trim()) return;
    try {
      await axios.post(`${API}/cases/${caseId}/notes`, { content: note }, { withCredentials: true });
      setNote('');
      fetchCase();
    } catch {}
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API}/cases/${caseId}`, { status: newStatus }, { withCredentials: true });
      setStatusUpdate(newStatus);
      fetchCase();
    } catch {}
  };

  const handleCreateInvoice = async () => {
    try {
      await axios.post(`${API}/invoices`, {
        case_id: caseId, client_id: caseData.client_id, items: invoiceItems, due_date: invoiceDue
      }, { withCredentials: true });
      setShowInvoice(false);
      setInvoiceItems([{ description: '', amount: 0 }]);
      setInvoiceDue('');
    } catch {}
  };

  const handleSubmitRating = async () => {
    if (!rating) return;
    try {
      await axios.post(`${API}/ratings`, {
        case_id: caseId, lawyer_id: caseData.lawyer_id, rating, comment: ratingComment
      }, { withCredentials: true });
      fetchCase();
    } catch {}
  };

  if (!caseData) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-[#B48E4B] border-t-transparent rounded-full animate-spin" />
      </div>
    </DashboardLayout>
  );

  const isAdminOrLawyer = user?.role === 'admin' || user?.role === 'lawyer';
  const backPath = user?.role === 'admin' ? '/admin' : user?.role === 'lawyer' ? '/lawyer' : '/client';

  return (
    <DashboardLayout>
      <div data-testid="case-detail-page">
        {/* Back button */}
        <button onClick={() => navigate(backPath)} className="flex items-center gap-2 text-[#475569] hover:text-[#0F172A] mb-6 text-sm" data-testid="back-button">
          <ArrowLeft className="w-4 h-4" /> {lang === 'ar' ? 'العودة' : 'Back'}
        </button>

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] font-heading">{caseData.title}</h1>
            <p className="text-[#475569] mt-1 capitalize">{caseData.case_type} &middot; {caseData.case_id}</p>
          </div>
          <CaseStatusBadge status={caseData.status} />
        </div>

        {/* Progress */}
        <Card className="border-slate-200 shadow-sm mb-6">
          <CardHeader>
            <CardTitle className="text-lg text-[#0F172A] font-heading">{t('case_progress')}</CardTitle>
          </CardHeader>
          <CardContent>
            <CaseProgressStepper currentStatus={caseData.status} />
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-[#0F172A] font-heading">{t('case_description')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#475569] leading-relaxed whitespace-pre-wrap">{caseData.description}</p>
              </CardContent>
            </Card>

            {/* Documents */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-[#0F172A] font-heading">{t('documents')}</CardTitle>
                <div>
                  <input type="file" ref={fileInputRef} onChange={handleUpload} className="hidden" multiple data-testid="file-upload-input" />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="bg-[#B48E4B] text-white hover:bg-[#8C6A32]"
                    data-testid="upload-document-button"
                  >
                    <Upload className="w-4 h-4 me-2" /> {uploading ? '...' : t('upload_document')}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {caseData.documents?.length > 0 ? (
                  <div className="space-y-2">
                    {caseData.documents.map(doc => (
                      <div key={doc.doc_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100" data-testid={`doc-${doc.doc_id}`}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-[#B48E4B]" />
                          <div>
                            <p className="text-sm font-medium text-[#0F172A]">{doc.original_filename}</p>
                            <p className="text-xs text-[#94A3B8]">{(doc.size / 1024).toFixed(1)} KB &middot; {doc.uploader_name}</p>
                          </div>
                        </div>
                        <button onClick={() => handleDownload(doc)} className="p-2 rounded hover:bg-slate-200 text-[#475569]" data-testid={`download-doc-${doc.doc_id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-slate-300 bg-slate-50 rounded-xl flex flex-col items-center justify-center p-8 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => fileInputRef.current?.click()} data-testid="upload-document-zone">
                    <Upload className="w-10 h-10 text-[#94A3B8] mb-3" />
                    <p className="text-sm text-[#475569]">{t('drag_drop')}</p>
                    <p className="text-xs text-[#94A3B8] mt-1">{t('supported_formats')}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-[#0F172A] font-heading">{t('notes')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder={t('add_note')}
                    className="flex-1 h-10"
                    onKeyDown={e => { if (e.key === 'Enter') handleAddNote(); }}
                    data-testid="note-input"
                  />
                  <Button onClick={handleAddNote} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="add-note-button">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {caseData.notes?.slice().reverse().map(n => (
                    <div key={n.note_id} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex items-center gap-2 mb-1">
                        <User className="w-3 h-3 text-[#94A3B8]" />
                        <span className="text-xs font-medium text-[#0F172A]">{n.author_name}</span>
                        <span className="text-xs text-[#94A3B8] capitalize">({n.author_role})</span>
                        <Clock className="w-3 h-3 text-[#94A3B8] ms-auto" />
                        <span className="text-xs text-[#94A3B8]">{new Date(n.created_at).toLocaleDateString()}</span>
                      </div>
                      <p className="text-sm text-[#475569]">{n.content}</p>
                    </div>
                  ))}
                  {(!caseData.notes || caseData.notes.length === 0) && (
                    <p className="text-center text-sm text-[#94A3B8] py-4">{lang === 'ar' ? 'لا توجد ملاحظات' : 'No notes yet'}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Case info */}
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-6 space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">{t('client')}</p>
                  <p className="text-sm font-medium text-[#0F172A]">{caseData.client_name || '-'}</p>
                  <p className="text-xs text-[#94A3B8]">{caseData.client_email || ''}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">{t('lawyer')}</p>
                  <p className="text-sm font-medium text-[#0F172A]">{caseData.lawyer_name || (lang === 'ar' ? 'غير معين' : 'Unassigned')}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">{lang === 'ar' ? 'تاريخ الإنشاء' : 'Created'}</p>
                  <p className="text-sm text-[#475569]">{new Date(caseData.created_at).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>

            {/* Status update (admin/lawyer only) */}
            {isAdminOrLawyer && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-sm text-[#0F172A] font-heading">{lang === 'ar' ? 'تحديث الحالة' : 'Update Status'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={statusUpdate} onValueChange={handleStatusChange}>
                    <SelectTrigger data-testid="status-update-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CASE_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{t(`status_${s}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Generate Invoice (admin/lawyer) */}
            {isAdminOrLawyer && (
              <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4">
                  {!showInvoice ? (
                    <Button onClick={() => setShowInvoice(true)} className="w-full bg-[#B48E4B] text-white hover:bg-[#8C6A32]" data-testid="generate-invoice-btn">
                      <DollarSign className="w-4 h-4 me-2" /> {lang === 'ar' ? 'إنشاء فاتورة' : 'Generate Invoice'}
                    </Button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-[#0F172A]">{lang === 'ar' ? 'فاتورة جديدة' : 'New Invoice'}</p>
                      {invoiceItems.map((item, i) => (
                        <div key={i} className="flex gap-2">
                          <Input placeholder={lang === 'ar' ? 'وصف' : 'Description'} value={item.description} onChange={e => { const items = [...invoiceItems]; items[i].description = e.target.value; setInvoiceItems(items); }} className="flex-1 h-9 text-sm" />
                          <Input type="number" placeholder="0" value={item.amount || ''} onChange={e => { const items = [...invoiceItems]; items[i].amount = parseFloat(e.target.value) || 0; setInvoiceItems(items); }} className="w-20 h-9 text-sm" />
                        </div>
                      ))}
                      <button onClick={() => setInvoiceItems([...invoiceItems, { description: '', amount: 0 }])} className="text-xs text-[#B48E4B]">+ {lang === 'ar' ? 'بند' : 'Item'}</button>
                      <Input type="date" value={invoiceDue} onChange={e => setInvoiceDue(e.target.value)} className="h-9 text-sm" placeholder="Due date" />
                      <div className="flex gap-2">
                        <Button onClick={handleCreateInvoice} size="sm" className="flex-1 bg-[#0F172A] text-white text-xs" data-testid="submit-case-invoice">{lang === 'ar' ? 'إنشاء' : 'Create'}</Button>
                        <Button onClick={() => setShowInvoice(false)} size="sm" variant="outline" className="text-xs">{lang === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Client Rating (only for resolved/closed cases) */}
            {user?.role === 'client' && (caseData.status === 'resolved' || caseData.status === 'closed') && (
              <Card className="border-slate-200 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-sm text-[#0F172A] font-heading">{lang === 'ar' ? 'تقييم الخدمة' : 'Rate Service'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {existingRating ? (
                    <div>
                      <div className="flex gap-1 mb-2">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-5 h-5 ${s <= existingRating.rating ? 'fill-[#B48E4B] text-[#B48E4B]' : 'text-slate-200'}`} />
                        ))}
                      </div>
                      {existingRating.comment && <p className="text-sm text-[#475569]">{existingRating.comment}</p>}
                      <p className="text-xs text-[#94A3B8] mt-1">{lang === 'ar' ? 'شكراً لتقييمك' : 'Thank you for your rating'}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex gap-1">
                        {[1,2,3,4,5].map(s => (
                          <button key={s} onClick={() => setRating(s)} data-testid={`star-${s}`}>
                            <Star className={`w-6 h-6 cursor-pointer transition-colors ${s <= rating ? 'fill-[#B48E4B] text-[#B48E4B]' : 'text-slate-200 hover:text-[#B48E4B]/50'}`} />
                          </button>
                        ))}
                      </div>
                      <textarea value={ratingComment} onChange={e => setRatingComment(e.target.value)} placeholder={lang === 'ar' ? 'شاركنا رأيك...' : 'Share your experience...'} className="w-full rounded-md border border-slate-200 p-2 text-sm min-h-[60px]" data-testid="rating-comment" />
                      <Button onClick={handleSubmitRating} disabled={!rating} className="w-full bg-[#B48E4B] text-white hover:bg-[#8C6A32] text-sm" data-testid="submit-rating">
                        {lang === 'ar' ? 'إرسال التقييم' : 'Submit Rating'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
